import { ASSESSMENT_TYPES, AssessmentTypeKey } from '@/modules/shared/hr-form-engine';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import { getPmReviews, REVIEW_QUERY_KEYS } from '@/modules/performance/hr-review-core';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { createdInMonthClause, ReviewMonth } from '../utils/review-months';

/** How many reviews of each type fall in `month` within `departmentCondition` (one row read per type). */
export const countReviewsByType = async (
  month: ReviewMonth,
  departmentCondition?: string | { $in: string[] }
): Promise<Record<AssessmentTypeKey, number>> => {
  const totals = await Promise.all(
    ASSESSMENT_TYPES.map(async ({ key }) => {
      const page = await getPmReviews({
        pageNo: 1,
        pageSize: 1,
        filter: {
          AssessmentType: key,
          ...createdInMonthClause(month),
          ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
        },
      });
      return [key, page.totalCount] as const;
    })
  );
  return Object.fromEntries(totals) as Record<AssessmentTypeKey, number>;
};

/** The All Reviews tab counts for one month, in the viewer's department scope. */
export const useReviewTypeCounts = (month: ReviewMonth) => {
  const { getDepartmentIdCondition, isLoading: isScopeLoading } = useDepartmentScope();
  const departmentCondition = getDepartmentIdCondition('');
  return useGlobalQuery({
    queryKey: [REVIEW_QUERY_KEYS.reviews, 'type-counts', month, departmentCondition],
    queryFn: () => countReviewsByType(month, departmentCondition),
    enabled: !isScopeLoading,
    staleTime: 60 * 1000,
  });
};
